// §4.4 MPS board (`/planning/mps`): the facility's open plan, else its
// approved plan, as a product by week grid of effective quantities with the
// demand, receipts, projection and proposal beneath each product. Planners
// override cells with a reason and submit; facility and group admins approve
// or reject on the plan itself (D-231, D-350).

import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import type { InfiniteData } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import {
  BucketGrid,
  type BucketCommitAnswer,
  type BucketGridCell,
  type BucketGridLabels,
  type BucketGridRow,
  type BucketGridWeek,
} from "../../../../components/bucket-grid";
import { formatGridQty } from "../../../../components/bucket-grid-paste";
import { Button } from "../../../../components/button";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { CheckboxField, Field } from "../../../../components/field";
import { Modal } from "../../../../components/modal";
import { EmptyState, ErrorState } from "../../../../components/states";
import { Banner } from "../../../../components/toast";
import { formatBusinessDate, formatTimestamp } from "../../../../lib/datetime";
import { UnauthorizedError } from "../../../../lib/problem";
import { RequestError, requestIdOf } from "../../../../lib/request";
import { MpsAutoMark, MpsStatusChip } from "../components/mps-bits";
import { useDemandRefreshes } from "../hooks/demand-queries";
import {
  MPS_NOTE_MAX,
  approveMps,
  flattenPages,
  isEditableStatus,
  isOpenStatus,
  mpsKeys,
  overrideMpsLine,
  proposeMps,
  rejectMps,
  submitMps,
  useCurrentMpsPlan,
  useMemberNames,
  useMpsPlanPages,
  useMpsPlans,
  useMpsRefresh,
  type MpsLine,
  type MpsPlan,
  type MpsPlanDetail,
  type MpsPlanSummary,
  type MpsReceiptDetail,
} from "../hooks/mps-queries";
import { usePlanningPermissions } from "../hooks/permissions";
import {
  problemCode,
  problemExtra,
  usePlanningParameters,
  usePlanningSettings,
  useProductIndex,
  useUoms,
  type Product,
} from "../hooks/planning-queries";
import {
  aboveBar,
  cellChange,
  cellKey,
  decidingUnit,
  groupByPlanner,
  indexLines,
  lineCellState,
  planWeeks,
  productChanges,
} from "../lib/mps-lib";
import { useOffline, weekLabel } from "../lib/planning-lib";
import { PlanningHeader } from "../components/planning-header";
import { usePlanningFacility } from "../planning-layout";
import { gridLabels } from "./forecast-editor";
import { shortId } from "../../../../lib/short-id";

type Conflict = { readonly code: string } | undefined;
type Outcome =
  | { readonly kind: "ok" | "bad" | "warn"; readonly text: string; readonly testId: string }
  | undefined;

/** How many products the review banner names (§4.4's product-level deltas). */
const REVIEW_PRODUCTS = 5;

export function PlanningMpsScreen() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { facility } = usePlanningFacility();
  const perms = usePlanningPermissions();
  const canEdit = perms.has("planning.mps.edit");
  const canApprove = perms.has("planning.mps.approve");
  const offline = useOffline();
  const zone = facility.timezone;

  const current = useCurrentMpsPlan(facility.id);
  const [chosenId, setChosenId] = useState<string | undefined>(undefined);
  const viewingId = chosenId ?? current.data?.id;
  const pages = useMpsPlanPages(viewingId);
  const { plan, products, lines } = useMemo(() => flattenPages(pages.data?.pages), [pages.data]);
  const isCurrentView =
    plan !== undefined && (current.data?.id === plan.id || chosenId === undefined);

  const settings = usePlanningSettings(facility.id);
  const refreshes = useDemandRefreshes(facility.id, undefined);
  const relevant = usePlanningParameters(
    facility.id,
    { status: "active", mps_relevant: "true" },
    undefined,
  );
  // Served with the plan, read across the tenant, so a planner scoped to this
  // facility sees the facilities that sell its products (D-349 ruling 4).
  const destinations = (pages.data?.pages[0]?.transfer_destinations ?? []).map((d) => d.name);
  const productIndex = useProductIndex(products.map((p) => p.product_id));
  const uoms = useUoms();
  const members = useMemberNames(perms.canReadMembers);
  const refreshMps = useMpsRefresh();

  const [diff, setDiff] = useState(false);
  // The previous approved plan is read for the diff, and while a plan waits for
  // the review banner's product-level changes.
  const comparing = diff || plan?.status === "pending_approval";
  const previousId = comparing ? (plan?.previous_approved_plan_id ?? undefined) : undefined;
  const previous = useMpsPlanPages(previousId);
  const {
    hasNextPage: previousMore,
    isFetchingNextPage: previousFetching,
    fetchNextPage: previousNext,
  } = previous;
  useEffect(() => {
    if (previousMore && !previousFetching) void previousNext();
  }, [previousMore, previousFetching, previousNext]);

  const [conflict, setConflict] = useState<Conflict>(undefined);
  const [routed, setRouted] = useState<string | undefined>(undefined);
  const [outcome, setOutcome] = useState<Outcome>(undefined);
  const [busy, setBusy] = useState<"propose" | "submit" | undefined>(undefined);
  const [dialog, setDialog] = useState<"approve" | "reject" | "plans" | undefined>(undefined);

  useEffect(() => {
    setChosenId(undefined);
    setDiff(false);
    setConflict(undefined);
    setRouted(undefined);
    setOutcome(undefined);
  }, [facility.id]);

  useEffect(() => {
    setRouted(undefined);
  }, [plan?.id, plan?.status]);

  const readError = current.error ?? pages.error;
  useEffect(() => {
    if (readError instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [readError, navigate]);

  const lineIndex = useMemo(() => indexLines(lines), [lines]);
  const previousLines = useMemo(
    () => indexLines(flattenPages(previous.data?.pages).lines),
    [previous.data],
  );
  const previousReady =
    previousId !== undefined && previous.data !== undefined && !previous.hasNextPage;

  const time = (iso: string | null) => formatTimestamp(iso, locale, zone);
  const unitCode = (uomId: string | null | undefined) =>
    uoms.data?.items.find((u) => u.id === uomId)?.code;
  const personText = (id: string | null) =>
    id === null ? "" : (members.byId.get(id) ?? shortId(id));

  const afterWrite = async (planIds: readonly string[]) => {
    await refreshMps(facility.id, planIds);
  };

  const onFailure = (error: unknown, fallback: string, testId: string) => {
    if (error instanceof UnauthorizedError) {
      void navigate({ to: "/login" });
      return;
    }
    const code = problemCode(error);
    if (code === "MPS_PLAN_NOT_EDITABLE" || code === "MPS_PLAN_NOT_PENDING") {
      setConflict({ code });
      void afterWrite(plan !== undefined ? [plan.id] : []);
      return;
    }
    const known: Record<string, string> = {
      MPS_PLAN_PENDING_APPROVAL: t("planning.mps.errors.pending"),
      DEMAND_REFRESH_MISSING: t("planning.mps.errors.refreshMissing"),
      MPS_NO_RELEVANT_PRODUCTS: t("planning.mps.errors.noRelevant"),
    };
    setOutcome({
      kind: "bad",
      testId,
      text:
        code !== undefined && known[code] !== undefined
          ? known[code]
          : t(fallback, { requestId: requestIdOf(error) ?? "-" }),
    });
  };

  const propose = async () => {
    setBusy("propose");
    setOutcome(undefined);
    setConflict(undefined);
    try {
      const made = await proposeMps(facility.id);
      setChosenId(undefined);
      setOutcome({
        kind: "ok",
        testId: "planning-mps-proposed",
        text: t("planning.mps.outcome.proposed", { plan: made.plan_no }),
      });
      await afterWrite([made.id]);
    } catch (error) {
      onFailure(error, "planning.mps.errors.proposeFailed", "planning-mps-propose-failed");
    } finally {
      setBusy(undefined);
    }
  };

  const submit = async () => {
    if (plan === undefined) return;
    setBusy("submit");
    setOutcome(undefined);
    try {
      const after = await submitMps(plan.id);
      setOutcome(
        after.status === "approved"
          ? {
              kind: "ok",
              testId: "planning-mps-auto-approved",
              text: t("planning.mps.outcome.autoApproved", { plan: after.plan_no }),
            }
          : {
              kind: "ok",
              testId: "planning-mps-submitted",
              text: t("planning.mps.outcome.submitted", { plan: after.plan_no }),
            },
      );
      await afterWrite([plan.id]);
    } catch (error) {
      onFailure(error, "planning.mps.errors.submitFailed", "planning-mps-submit-failed");
    } finally {
      setBusy(undefined);
    }
  };

  const commitCell = (
    productId: string,
    week: string,
    value: string | null,
    reason: string | undefined,
  ): Promise<BucketCommitAnswer> | undefined => {
    if (plan === undefined) return undefined;
    const line = lineIndex.get(cellKey(productId, week));
    if (line === undefined) return undefined;
    return (async (): Promise<BucketCommitAnswer> => {
      try {
        const saved = await overrideMpsLine(plan.id, line.id, {
          override_qty: value,
          override_reason: reason ?? null,
          expected_updated_at: line.updated_at,
        });
        patchPlan(qc, plan.id, saved.plan, saved.line);
        void afterWrite([plan.id]);
        return { status: "committed" };
      } catch (error) {
        if (error instanceof UnauthorizedError) {
          void navigate({ to: "/login" });
          return { status: "refused", message: "" };
        }
        const code = problemCode(error);
        if (code === "MPS_LINE_CHANGED") {
          const now = problemExtra(error, "current") as MpsLine | undefined;
          if (now !== undefined) patchPlan(qc, plan.id, undefined, now);
          void afterWrite([plan.id]);
          return {
            status: "refused",
            message: t("planning.mps.errors.lineChanged"),
            current: now?.effective_qty ?? null,
          };
        }
        if (code === "MPS_PLAN_NOT_EDITABLE") {
          setConflict({ code });
          void afterWrite([plan.id]);
          return { status: "refused", message: t("planning.mps.conflict.changed") };
        }
        if (code === "MPS_LINE_FROZEN") {
          return { status: "refused", message: t("planning.mps.errors.frozen") };
        }
        if (code === "MPS_OVERRIDE_REASON_REQUIRED") {
          return { status: "refused", message: t("planning.mps.errors.reasonRequired") };
        }
        return {
          status: "refused",
          message:
            error instanceof RequestError && error.httpStatus === 400
              ? t("planning.mps.errors.invalid")
              : t("planning.common.saveFailed", { requestId: requestIdOf(error) ?? "-" }),
        };
      }
    })();
  };

  // ---- states ----------------------------------------------------------------

  const head = (actions: ReactNode, meta?: ReactNode) => (
    <>
      <PlanningHeader title={t("planning.mps.title")} actions={actions} />
      {meta}
    </>
  );

  const proposeButton = (testId = "planning-mps-propose") =>
    canEdit ? (
      <Button
        busy={busy === "propose"}
        busyLabel={t("planning.mps.proposing")}
        disabled={offline || busy !== undefined}
        data-testid={testId}
        onClick={() => void propose()}
      >
        {t("planning.mps.proposeNow")}
      </Button>
    ) : null;

  const outcomeBanner =
    outcome !== undefined ? (
      <Banner kind={outcome.kind} data-testid={outcome.testId}>
        {outcome.text}
      </Banner>
    ) : null;

  if (readError !== null && !(readError instanceof UnauthorizedError)) {
    return (
      <section className="planning-screen" data-testid="planning-mps">
        {head(null)}
        <ErrorState
          title={t("planning.mps.loadError")}
          retryLabel={t("planning.common.retry")}
          onRetry={() => {
            void current.refetch();
            void pages.refetch();
          }}
          requestId={requestIdOf(readError)}
          requestIdLabel={t("planning.common.requestId")}
        />
      </section>
    );
  }

  if (current.isPending || (viewingId !== undefined && plan === undefined)) {
    return (
      <section className="planning-screen" data-testid="planning-mps">
        {head(null)}
        <BucketGrid
          rows={[]}
          weeks={[]}
          locale={locale}
          labels={boardLabels(t)}
          loading
          loadingLabel={t("planning.mps.loading")}
          data-testid="planning-mps-grid"
        />
      </section>
    );
  }

  if (plan === undefined) {
    const succeeded = (refreshes.data?.items ?? []).some((r) => r.status === "succeeded");
    const noProducts = relevant.data?.items.length === 0;
    let empty: ReactNode;
    if (refreshes.data !== undefined && !succeeded) {
      empty = (
        <div data-testid="planning-mps-empty" data-reason="no_refresh">
          <EmptyState
            title={t("planning.mps.empty.noRefreshTitle")}
            body={t("planning.mps.empty.noRefreshBody")}
          />
        </div>
      );
    } else if (noProducts) {
      empty = (
        <div
          data-testid="planning-mps-empty"
          data-reason="no_products"
          className="planning-mps-empty"
        >
          <EmptyState
            title={t("planning.mps.empty.noProductsTitle")}
            body={t("planning.mps.empty.noProductsBody")}
          />
          <Link to="/planning/settings/parameters" data-testid="planning-mps-to-parameters">
            {t("planning.mps.empty.toParameters")}
          </Link>
        </div>
      );
    } else {
      empty = (
        <div
          data-testid="planning-mps-empty"
          data-reason="not_proposed"
          className="planning-mps-empty"
        >
          <EmptyState
            title={t("planning.mps.empty.notProposedTitle")}
            body={t("planning.mps.empty.notProposedBody")}
          />
        </div>
      );
    }
    return (
      <section className="planning-screen" data-testid="planning-mps">
        {head(noProducts ? null : proposeButton())}
        {outcomeBanner}
        {empty}
      </section>
    );
  }

  // ---- the plan ----------------------------------------------------------------

  const status = plan.status;
  const editable = isCurrentView && isEditableStatus(status) && canEdit;
  const pending = status === "pending_approval";
  const readOnlyView = !isCurrentView;
  const weeks = planWeeks(plan.horizon_start, plan.horizon_weeks);
  const gridWeeks: BucketGridWeek[] = weeks.map((w) => ({ start: w, label: weekLabel(w, locale) }));
  const showDiff = diff && plan.previous_approved_plan_id !== null;

  const rowsFor = (group: readonly { product_id: string; base_uom_id: string | null }[]) =>
    group.map((p): BucketGridRow => {
      const product = productIndex.byId.get(p.product_id);
      const unit = unitCode(p.base_uom_id ?? product?.base_uom_id);
      const cells: Record<string, BucketGridCell> = {};
      const demand: Record<string, string | null> = {};
      const receipts: Record<string, string | null> = {};
      const receiptNotes: Record<string, string> = {};
      const projected: Record<string, string | null> = {};
      const proposed: Record<string, string | null> = {};
      for (const week of weeks) {
        const line = lineIndex.get(cellKey(p.product_id, week));
        if (line === undefined) continue;
        const { state, locked } = lineCellState(line);
        const note = stateNoteOf(line, t, { time, personText, planNo: planNoOf });
        cells[week] = {
          value: showDiff
            ? previousReady
              ? cellChange(line, previousLines.get(cellKey(p.product_id, week)))
              : null
            : line.effective_qty,
          state,
          ...(locked ? { locked: true } : {}),
          ...(note !== undefined ? { stateNote: note } : {}),
        };
        demand[week] = line.demand_qty;
        receipts[week] = line.scheduled_receipts_qty;
        projected[week] = line.projected_on_hand_effective;
        proposed[week] = line.proposed_qty;
        if (line.receipt_detail.length > 0 || Number(line.released_qty) > 0) {
          receiptNotes[week] = receiptNote(line.receipt_detail, plan, unit, t, locale, line);
        }
      }
      const sku = product?.sku ?? shortId(p.product_id);
      return {
        key: p.product_id,
        labelText: sku,
        readOnly: !editable || showDiff,
        label: <ProductLabel product={product} sku={sku} unit={unit} />,
        cells,
        subRows: [
          { key: "demand", label: t("planning.mps.sub.demand"), values: demand },
          {
            key: "receipts",
            label: t("planning.mps.sub.receipts"),
            values: receipts,
            notes: receiptNotes,
          },
          { key: "projected", label: t("planning.mps.sub.projected"), values: projected },
          { key: "proposed", label: t("planning.mps.sub.proposed"), values: proposed },
        ],
      };
    });

  const groups = groupByPlanner(products);
  const labels = boardLabels(t);
  const faBar = settings.data?.mps_facility_admin_delta_pct;
  const deciding = decidingUnit(plan.delta_by_uom);
  const changes =
    pending && previousReady && plan.previous_approved_plan_id !== null
      ? productChanges(lines, previousLines).slice(0, REVIEW_PRODUCTS)
      : [];

  const actions = (
    <>
      <Button
        size="sm"
        data-testid="planning-mps-plans-open"
        onClick={() => {
          setDialog("plans");
        }}
      >
        {t("planning.mps.plans")}
      </Button>
      {isCurrentView && !pending ? proposeButton() : null}
      {editable ? (
        <Button
          variant="primary"
          busy={busy === "submit"}
          busyLabel={t("planning.mps.submitting")}
          disabled={offline || busy !== undefined}
          data-testid="planning-mps-submit"
          onClick={() => void submit()}
        >
          {t("planning.mps.submit")}
        </Button>
      ) : null}
      {isCurrentView && pending && canApprove ? (
        <>
          <Button
            variant="danger"
            disabled={offline}
            data-testid="planning-mps-reject"
            onClick={() => {
              setDialog("reject");
            }}
          >
            {t("planning.mps.reject")}
          </Button>
          <Button
            variant="primary"
            disabled={offline}
            data-testid="planning-mps-approve"
            onClick={() => {
              setDialog("approve");
            }}
          >
            {t("planning.mps.approve")}
          </Button>
        </>
      ) : null}
    </>
  );

  const meta = (
    <p className="planning-mps-meta" data-testid="planning-mps-header" data-plan-id={plan.id}>
      <span className="planning-mps-no" data-testid="planning-mps-plan-no">
        {plan.plan_no}
      </span>
      <MpsStatusChip status={status} />
      <MpsAutoMark plan={plan} />
      <span>{t("planning.mps.proposedAt", { time: time(plan.proposed_at) })}</span>
    </p>
  );

  return (
    <section className="planning-screen" data-testid="planning-mps" data-plan-status={status}>
      {head(actions, meta)}
      {outcomeBanner}
      {offline && (canEdit || canApprove) ? (
        <p className="planning-note">{t("planning.common.offlineWrites")}</p>
      ) : null}

      {conflict !== undefined ? (
        <Banner kind="warn" data-testid="planning-mps-conflict" data-code={conflict.code}>
          <span className="planning-mps-banner-line">
            {status === "superseded"
              ? t("planning.mps.conflict.superseded")
              : t("planning.mps.conflict.changed")}
            {status === "superseded" && plan.superseded_by_plan_id !== null ? (
              <Button
                size="sm"
                data-testid="planning-mps-open-replacement"
                onClick={() => {
                  setConflict(undefined);
                  setChosenId(undefined);
                  void afterWrite([]);
                }}
              >
                {t("planning.mps.openReplacement")}
              </Button>
            ) : (
              <Button
                size="sm"
                data-testid="planning-mps-reload"
                onClick={() => {
                  setConflict(undefined);
                  void afterWrite([plan.id]);
                }}
              >
                {t("planning.mps.reload")}
              </Button>
            )}
          </span>
        </Banner>
      ) : null}

      {readOnlyView ? (
        <Banner kind="neutral" data-testid="planning-mps-earlier">
          <span className="planning-mps-banner-line">
            {status === "superseded"
              ? t("planning.mps.earlierSuperseded")
              : t("planning.mps.earlier")}
            <Button
              size="sm"
              data-testid="planning-mps-back"
              onClick={() => {
                setChosenId(undefined);
              }}
            >
              {t("planning.mps.backToCurrent")}
            </Button>
          </span>
        </Banner>
      ) : null}

      {routed !== undefined ? (
        <Banner kind="warn" data-testid="planning-mps-routed" data-delta-pct={routed}>
          {t("planning.mps.routed", { pct: pctText(routed, locale) })}
        </Banner>
      ) : null}

      {pending && plan.delta_pct !== null ? (
        <Banner
          kind={
            plan.previous_approved_plan_id === null ||
            (faBar !== undefined && aboveBar(plan.delta_pct, faBar))
              ? "warn"
              : "neutral"
          }
          data-testid="planning-mps-threshold"
          data-delta-pct={plan.delta_pct}
        >
          <span className="planning-mps-threshold">
            <span>
              {plan.previous_approved_plan_id === null
                ? t("planning.mps.threshold.first")
                : deciding !== undefined
                  ? t("planning.mps.threshold.delta", {
                      pct: pctText(deciding.pct, locale),
                      unit: unitCode(deciding.uomId) ?? "",
                    })
                  : t("planning.mps.threshold.deltaNoUnit", {
                      pct: pctText(plan.delta_pct, locale),
                    })}
            </span>
            {plan.previous_approved_plan_id !== null && faBar !== undefined ? (
              <span data-testid="planning-mps-threshold-bar">
                {aboveBar(plan.delta_pct, faBar)
                  ? t("planning.mps.threshold.aboveBar", { bar: pctText(faBar, locale) })
                  : t("planning.mps.threshold.withinBar", { bar: pctText(faBar, locale) })}
              </span>
            ) : null}
            {changes.length > 0 ? (
              <span className="planning-mps-changes" data-testid="planning-mps-product-changes">
                {t("planning.mps.threshold.products")}{" "}
                {changes.map((c, i) => {
                  const product = productIndex.byId.get(c.productId);
                  return (
                    <span key={c.productId} data-product-id={c.productId}>
                      {i > 0 ? ", " : ""}
                      {product?.sku ?? shortId(c.productId)} {formatGridQty(c.change, locale, true)}
                      {unitCode(product?.base_uom_id) !== undefined
                        ? ` ${unitCode(product?.base_uom_id) ?? ""}`
                        : ""}
                    </span>
                  );
                })}
              </span>
            ) : null}
          </span>
        </Banner>
      ) : null}

      {pending && (plan.demand_changed_cells ?? 0) > 0 ? (
        <Banner
          kind="warn"
          data-testid="planning-mps-demand-cells"
          data-count={plan.demand_changed_cells ?? 0}
        >
          {t("planning.mps.demandChangedCells", { count: plan.demand_changed_cells ?? 0 })}
        </Banner>
      ) : null}

      {isCurrentView && !pending && plan.demand_changed_since_proposal ? (
        <Banner kind="warn" data-testid="planning-mps-demand-changed">
          <span className="planning-mps-banner-line">
            {t("planning.mps.demandChanged")}
            {proposeButton("planning-mps-demand-changed-propose")}
          </span>
        </Banner>
      ) : null}

      {isOpenStatus(status) && !pending && plan.rejected_at !== null ? (
        <Banner kind="warn" data-testid="planning-mps-returned">
          {t("planning.mps.returned", {
            who: personText(plan.rejected_by),
            date: time(plan.rejected_at),
            note: plan.rejection_note ?? "",
          })}
        </Banner>
      ) : null}

      {destinations.length > 0 ? (
        <p className="planning-note" data-testid="planning-mps-transfer-note">
          {t("planning.mps.transferNote", { facilities: destinations.join(", ") })}
        </p>
      ) : null}

      <div className="planning-mps-bar">
        <p className="planning-mps-totals" data-testid="planning-mps-totals">
          <span>{t("planning.mps.totals")}</span>
          {plan.effective_totals.length === 0 ? (
            <span>0</span>
          ) : (
            plan.effective_totals.map((total) => (
              <span
                key={total.uom_id}
                className="num"
                data-testid="planning-mps-total"
                data-uom-id={total.uom_id}
                data-qty={total.qty}
              >
                {formatGridQty(total.qty, locale)}
                {unitCode(total.uom_id) !== undefined ? (
                  <span className="planning-unit"> {unitCode(total.uom_id)}</span>
                ) : null}
              </span>
            ))
          )}
        </p>
        {plan.previous_approved_plan_id !== null ? (
          <CheckboxField
            label={t("planning.mps.diff")}
            checked={diff}
            onChange={setDiff}
            data-testid="planning-mps-diff"
          />
        ) : null}
      </div>

      {showDiff && !previousReady ? (
        <p className="planning-hint" role="status">
          {t("planning.mps.diffLoading")}
        </p>
      ) : null}
      {editable ? <p className="planning-hint">{t("planning.mps.editHint")}</p> : null}

      {products.length === 0 ? (
        <div data-testid="planning-mps-empty" data-reason="no_lines">
          <EmptyState
            title={t("planning.mps.empty.noLinesTitle")}
            body={t("planning.mps.empty.noLinesBody")}
          />
        </div>
      ) : (
        groups.map((group) => (
          <div
            key={group.plannerCode ?? "none"}
            className="planning-mps-group"
            data-testid="planning-mps-group"
            data-planner-code={group.plannerCode ?? ""}
          >
            {groups.length > 1 ? (
              <h2 className="planning-mps-group-title">
                {group.plannerCode === null
                  ? t("planning.mps.noPlannerCode")
                  : t("planning.mps.plannerCode", { code: group.plannerCode })}
              </h2>
            ) : null}
            <BucketGrid
              rows={rowsFor(group.products)}
              weeks={gridWeeks}
              locale={locale}
              labels={labels}
              editable={editable && !showDiff && !offline}
              reasonRequired
              signed={showDiff}
              onCommit={(productId, week, value, reason) =>
                commitCell(productId, week, value, reason)
              }
              data-testid="planning-mps-grid"
            />
          </div>
        ))
      )}

      {pages.hasNextPage ? (
        <Button
          busy={pages.isFetchingNextPage}
          busyLabel={t("planning.mps.loadingMore")}
          data-testid="planning-mps-more"
          onClick={() => void pages.fetchNextPage()}
        >
          {t("planning.mps.loadMore")}
        </Button>
      ) : null}

      <p className="planning-hint" data-testid="planning-mps-receipts-hint">
        {t("planning.mps.receiptsHint", { time: time(plan.proposed_at) })}
      </p>

      {dialog === "plans" ? (
        <PlansDialog
          facilityId={facility.id}
          currentId={current.data?.id}
          viewingId={plan.id}
          time={time}
          onPick={(id) => {
            setChosenId(id === current.data?.id ? undefined : id);
            setDiff(false);
            setDialog(undefined);
          }}
          onClose={() => {
            setDialog(undefined);
          }}
        />
      ) : null}
      {dialog === "approve" ? (
        <DecisionDialog
          kind="approve"
          plan={plan}
          onClose={() => {
            setDialog(undefined);
          }}
          onDone={(after) => {
            setDialog(undefined);
            setOutcome({
              kind: "ok",
              testId: "planning-mps-approved",
              text: t("planning.mps.outcome.approved", { plan: after.plan_no }),
            });
            void afterWrite([plan.id]);
          }}
          onRouted={(pct) => {
            setDialog(undefined);
            setRouted(pct);
            void afterWrite([plan.id]);
          }}
          onConflict={(code) => {
            setDialog(undefined);
            setConflict({ code });
            void afterWrite([plan.id]);
          }}
        />
      ) : null}
      {dialog === "reject" ? (
        <DecisionDialog
          kind="reject"
          plan={plan}
          onClose={() => {
            setDialog(undefined);
          }}
          onDone={(after) => {
            setDialog(undefined);
            setOutcome({
              kind: "ok",
              testId: "planning-mps-rejected",
              text: t("planning.mps.outcome.rejected", { plan: after.plan_no }),
            });
            void afterWrite([plan.id]);
          }}
          onRouted={() => undefined}
          onConflict={(code) => {
            setDialog(undefined);
            setConflict({ code });
            void afterWrite([plan.id]);
          }}
        />
      ) : null}
    </section>
  );

  function planNoOf(id: string | null): string {
    if (id === null) return "";
    if (id === plan?.id) return plan.plan_no;
    if (id === plan?.previous_approved_plan_id) return t("planning.mps.previousPlan");
    return shortId(id);
  }
}

// ---- pieces ------------------------------------------------------------------

function ProductLabel({
  product,
  sku,
  unit,
}: {
  product: Product | undefined;
  sku: string;
  unit: string | undefined;
}) {
  return (
    <span className="planning-product">
      <span className="planning-sku">{sku}</span>
      {product !== undefined ? <span className="planning-product-name">{product.name}</span> : null}
      {unit !== undefined ? <span className="planning-unit">{unit}</span> : null}
    </span>
  );
}

export function boardLabels(t: TFunction): BucketGridLabels {
  return { ...gridLabels(t), empty: t("planning.mps.empty.noLinesBody") };
}

/** A two-decimal percentage in the reader's locale. */
export function pctText(pct: string, locale: string): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(Number(pct));
}

/** The words of an overridden or dropped cell's popover (spec 03 §4.4). */
export function stateNoteOf(
  line: MpsLine,
  t: TFunction,
  fmt: {
    time: (iso: string | null) => string;
    personText: (id: string | null) => string;
    planNo: (id: string | null) => string;
  },
): string | undefined {
  if (line.override_qty !== null) {
    return t("planning.mps.note.overridden", {
      reason: line.override_reason ?? "",
      who: fmt.personText(line.override_by),
      time: fmt.time(line.override_at),
    });
  }
  if (line.override_dropped) {
    return t(
      line.dropped_cause === "frozen"
        ? "planning.mps.note.droppedFrozen"
        : "planning.mps.note.droppedChanged",
      {
        qty: line.dropped_override_qty ?? "",
        reason: line.dropped_override_reason ?? "",
        plan: fmt.planNo(line.dropped_from_plan_id),
      },
    );
  }
  return undefined;
}

/**
 * The receipts tooltip: each work order and transfer, and when they were read
 * (§4.4), then the part of a frozen quantity those orders already carry
 * (D-382 ruling 8).
 */
export function receiptNote(
  detail: readonly MpsReceiptDetail[],
  plan: Pick<MpsPlan, "horizon_start">,
  unit: string | undefined,
  t: TFunction,
  locale: string,
  line?: Pick<MpsLine, "released_qty"> & Partial<Pick<MpsLine, "released_detail">>,
): string {
  const parts = detail.map((r) => {
    const qty = `${formatGridQty(r.qty, locale)}${unit !== undefined ? ` ${unit}` : ""}`;
    const kind = t(`planning.mps.receipt.${r.kind}`, { ref: r.ref_no, qty });
    if (r.due === null) return `${kind}, ${t("planning.mps.receipt.noDue")}`;
    if (r.due < plan.horizon_start) {
      return `${kind}, ${t("planning.mps.receipt.overdue", { date: formatBusinessDate(r.due, locale) })}`;
    }
    return `${kind}, ${t("planning.mps.receipt.due", { date: formatBusinessDate(r.due, locale) })}`;
  });
  if (line !== undefined && Number(line.released_qty) > 0) {
    const qty = `${formatGridQty(line.released_qty, locale)}${unit !== undefined ? ` ${unit}` : ""}`;
    parts.push(t("planning.mps.receipt.released", { qty }));
    // The orders that hold it, by number (§4.3, D-395 ruling 72).
    for (const r of line.released_detail ?? []) {
      const held = `${formatGridQty(r.qty, locale)}${unit !== undefined ? ` ${unit}` : ""}`;
      parts.push(
        t(`planning.mps.receipt.heldBy.${r.kind}`, {
          ref: r.ref_no,
          qty: held,
          date: formatBusinessDate(r.due, locale),
        }),
      );
    }
  }
  return parts.join("; ");
}

/** Replaces a line and the header in every loaded page, ahead of the re-read. */
function patchPlan(
  qc: ReturnType<typeof useQueryClient>,
  planId: string,
  header: MpsPlan | undefined,
  line: MpsLine | undefined,
): void {
  qc.setQueryData<InfiniteData<MpsPlanDetail>>(mpsKeys.plan(planId), (old) => {
    if (old === undefined) return old;
    return {
      ...old,
      pages: old.pages.map((page) => ({
        ...page,
        plan: header ?? page.plan,
        lines:
          line === undefined ? page.lines : page.lines.map((l) => (l.id === line.id ? line : l)),
      })),
    };
  });
}

function PlansDialog(props: {
  facilityId: string;
  currentId: string | undefined;
  viewingId: string;
  time: (iso: string | null) => string;
  onPick: (id: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const page = cursors.length - 1;
  const list = useMpsPlans(props.facilityId, cursors[page], true);
  const columns: DataTableColumn<MpsPlanSummary>[] = [
    {
      key: "plan_no",
      header: t("planning.mps.columns.plan"),
      id: true,
      render: (row) => (
        <span data-testid="planning-mps-plan-row-no">
          {row.plan_no}
          {row.id === props.currentId ? ` · ${t("planning.mps.columns.current")}` : ""}
        </span>
      ),
    },
    {
      key: "status",
      header: t("planning.mps.columns.status"),
      render: (row) => (
        <span className="planning-mps-chips">
          <MpsStatusChip status={row.status} testId="planning-mps-plan-row-status" />
          <MpsAutoMark plan={row} />
        </span>
      ),
    },
    {
      key: "proposed",
      header: t("planning.mps.columns.proposed"),
      age: true,
      render: (row) => props.time(row.proposed_at),
    },
    {
      key: "delta",
      header: t("planning.mps.columns.delta"),
      numeric: true,
      render: (row) => (row.delta_pct === null ? "" : `${row.delta_pct} %`),
    },
  ];
  return (
    <Modal
      label={t("planning.mps.plans")}
      heading={t("planning.mps.plans")}
      closeLabel={t("planning.common.close")}
      onClose={props.onClose}
      className="planning-drawer"
      testId="planning-mps-plans"
    >
      <DataTable
        columns={columns}
        rows={list.data?.items ?? []}
        rowKey={(r) => r.id}
        rowTestId="planning-mps-plan-row"
        rowProps={(row) => ({ "data-plan-id": row.id, "data-status": row.status })}
        rowClassName={(row) =>
          row.id === props.viewingId ? "planning-mps-row-viewing" : undefined
        }
        onRowClick={(row) => {
          props.onPick(row.id);
        }}
        loading={list.isPending}
        loadingLabel={t("planning.mps.loading")}
        caption={t("planning.mps.plans")}
        empty={<EmptyState title={t("planning.mps.plansEmpty")} body="" />}
        error={
          list.error !== null ? (
            <ErrorState
              title={t("planning.mps.loadError")}
              retryLabel={t("planning.common.retry")}
              onRetry={() => void list.refetch()}
              requestId={requestIdOf(list.error)}
              requestIdLabel={t("planning.common.requestId")}
            />
          ) : undefined
        }
        pager={{
          range: t("planning.common.page", { page: page + 1 }),
          page: page + 1,
          onPage: (n) => {
            if (n > page + 1 && list.data?.next_cursor)
              setCursors([...cursors, list.data.next_cursor]);
            else if (n < page + 1) setCursors(cursors.slice(0, -1));
          },
          prevLabel: t("planning.common.previous"),
          nextLabel: t("planning.common.next"),
          hasPrev: page > 0,
          hasNext: (list.data?.next_cursor ?? null) !== null,
        }}
      />
    </Modal>
  );
}

function DecisionDialog(props: {
  kind: "approve" | "reject";
  plan: MpsPlan;
  onClose: () => void;
  onDone: (after: MpsPlan) => void;
  onRouted: (deltaPct: string) => void;
  onConflict: (code: string) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<string | undefined>(undefined);
  const reject = props.kind === "reject";
  const trimmed = note.trim();

  const send = async () => {
    setSending(true);
    setFailure(undefined);
    try {
      const after = reject
        ? await rejectMps(props.plan.id, trimmed)
        : await approveMps(props.plan.id, trimmed.length > 0 ? trimmed : undefined);
      props.onDone(after);
    } catch (error) {
      if (error instanceof UnauthorizedError) {
        void navigate({ to: "/login" });
        return;
      }
      const code = problemCode(error);
      if (code === "APPROVAL_THRESHOLD_EXCEEDED") {
        const pct = problemExtra(error, "delta_pct");
        props.onRouted(typeof pct === "string" ? pct : (props.plan.delta_pct ?? ""));
        return;
      }
      if (code === "MPS_PLAN_NOT_PENDING") {
        props.onConflict(code);
        return;
      }
      setFailure(t("planning.common.saveFailed", { requestId: requestIdOf(error) ?? "-" }));
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      label={reject ? t("planning.mps.rejectTitle") : t("planning.mps.approveTitle")}
      heading={reject ? t("planning.mps.rejectTitle") : t("planning.mps.approveTitle")}
      closeLabel={t("planning.common.close")}
      onClose={props.onClose}
      pending={sending}
      className="planning-dialog"
      testId={reject ? "planning-mps-reject-dialog" : "planning-mps-approve-dialog"}
    >
      <div className="planning-mps-decision">
        <p>
          {reject
            ? t("planning.mps.rejectBody", { plan: props.plan.plan_no })
            : t("planning.mps.approveBody", { plan: props.plan.plan_no })}
        </p>
        <Field
          label={reject ? t("planning.mps.noteRequired") : t("planning.mps.noteOptional")}
          wide
        >
          <textarea
            rows={3}
            maxLength={MPS_NOTE_MAX}
            value={note}
            data-testid="planning-mps-note"
            onChange={(e) => {
              setNote(e.target.value);
            }}
          />
        </Field>
        {failure !== undefined ? (
          <Banner kind="bad" data-testid="planning-mps-decision-failed">
            {failure}
          </Banner>
        ) : null}
        <div className="planning-mps-decision-actions">
          <Button disabled={sending} onClick={props.onClose}>
            {t("planning.common.cancel")}
          </Button>
          <Button
            variant={reject ? "danger" : "primary"}
            busy={sending}
            busyLabel={t("planning.common.saving")}
            disabled={reject && trimmed.length === 0}
            data-testid={reject ? "planning-mps-reject-confirm" : "planning-mps-approve-confirm"}
            onClick={() => void send()}
          >
            {reject ? t("planning.mps.reject") : t("planning.mps.approve")}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
